import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { emailService } from '@/services/email'
import type { ClientFormData, Client } from '@/types/client'

interface CreateClientData extends ClientFormData {
  sendWelcomeEmail?: boolean
  assignedAdminId?: string
}

export function useCreateClient() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (data: CreateClientData): Promise<Client> => {
      const fullName = `${data.prenom} ${data.nom}`

      // Create the client profile in the users table
      const { data: clientData, error: clientError } = await supabase
        .from('users')
        .insert({
          email: data.email,
          full_name: fullName,
          phone: data.phone || null,
          type_client: data.type_client,
          company: data.type_client === 'professionnel' ? data.company : null,
          siret: data.type_client === 'professionnel' ? (data.siret || null) : null,
          tva_intracommunautaire:
            data.type_client === 'professionnel' ? (data.tva_intracommunautaire || null) : null,
          adresse_facturation: data.adresse_facturation || data.adresse || null,
          role: 'client',
        })
        .select()
        .single()

      if (clientError) throw clientError

      // Assign to the admin who created the client or specified admin
      const adminId = data.assignedAdminId || user?.id
      if (adminId && clientData) {
        await supabase.from('client_admin_assignments').insert({
          client_id: clientData.id,
          admin_id: adminId,
          is_owner: true,
          can_edit: true,
          can_view: true,
        })
      }

      // Send welcome email if requested
      if (data.sendWelcomeEmail && clientData) {
        try {
          // Le template 'welcome' du worker attend les variables name et dashboardUrl
          await emailService.sendTemplateEmail({
            to: data.email,
            template: 'welcome',
            variables: {
              name: fullName,
              dashboardUrl: 'https://client.lessablesweb.fr',
            },
          })
        } catch (emailError) {
          // Don't fail client creation if email fails
          console.error('Failed to send welcome email:', emailError)
        }
      }

      return clientData as Client
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}
